import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const ManagerMobileHeader = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;
        const { data } = await supabase?.from('users')?.select('name, email, is_paid, role, responsible_servers')?.eq('id', user?.id)?.single();
        if (data) setProfile(data);
      } catch (e) {}
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) setDropdownOpen(false);
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const getInitials = (n) => {
    if (!n) return '?';
    const parts = n?.trim()?.split(' ');
    if (parts?.length >= 2) return (parts?.[0]?.charAt(0) + parts?.[1]?.charAt(0))?.toUpperCase();
    return n?.trim()?.charAt(0)?.toUpperCase();
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await supabase?.auth?.signOut();
    navigate('/login-authentication');
  };

  return (
    <div className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: '#0a0a0a', borderColor: '#1a1a1a', paddingTop: 'max(12px, env(safe-area-inset-top))', minHeight: 'calc(56px + env(safe-area-inset-top))' }}>
      <div className="flex items-center gap-3">
        {showBack ? (
          <button onClick={() => navigate(-1)} className="w-11 h-11 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.7)' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ) : (
          <img src="/logo-noirock.png" alt="NoiRock" className="w-8 h-8 rounded-lg flex-shrink-0" />
        )}
        {title && <span className="text-white font-bold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>{title}</span>}
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(v => !v)}
          className="flex items-center justify-center rounded-full font-bold text-white"
          style={{ width: '44px', height: '44px', backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif', fontSize: '14px', border: 'none' }}
        >
          {getInitials(profile?.name)}
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-2 rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#2a2a2a', minWidth: '200px', zIndex: 100 }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: '#2a2a2a' }}>
              <p className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Sora, sans-serif' }}>{profile?.name || 'Manager'}</p>
              <p className="text-xs truncate mt-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{profile?.email || ''}</p>
            </div>

            <div className="py-1">
              <button
                onClick={() => { setDropdownOpen(false); navigate('/manager/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif', fontSize: '14px', background: 'none', border: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4" /><path d="M2 14c0-2.761 2.686-5 6-5s6 2.239 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                {t('nav.profile')}
              </button>

              <button
                onClick={() => { setDropdownOpen(false); navigate('/manager/notifications'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif', fontSize: '14px', background: 'none', border: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2a4 4 0 00-4 4v2.5L2.5 11h11L12 8.5V6a4 4 0 00-4-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M6.5 12.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.4" /></svg>
                {t('nav.notifications')}
              </button>

              <div className="border-t my-1" style={{ borderColor: '#2a2a2a' }} />

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{ color: 'rgba(239,68,68,0.8)', fontFamily: 'Inter, sans-serif', fontSize: '14px', background: 'none', border: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                {t('nav.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerMobileHeader;
